import { AIRFIELD } from '../core/SimConfig.js';
import { DEG2RAD } from '../utils.js';

/**
 * Runway-relative coordinate frame for runway 35.
 *
 * Training missions are authored in runway coordinates — "40 metres short of the
 * threshold, 43 metres right of the centreline" — rather than raw world XZ, so
 * the traffic pattern stays glued to the runway if the airfield ever moves.
 *
 *   along   (t) metres down the runway in the departure direction
 *   lateral (s) metres right of the centreline, looking down the runway
 *
 * The basis is derived once from {@link AIRFIELD.runway35}: `d` runs down the
 * runway (the forward vector of the runway's scenario heading) and `r` is 90°
 * right of it in the XZ plane.
 */

const RWY = AIRFIELD.runway35;

const _hdg = RWY.heading * DEG2RAD;
// Scenario headings map to a forward vector of (-sin h, -cos h) in XZ.
const DX = -Math.sin(_hdg);
const DZ = -Math.cos(_hdg);
// Right of forward in XZ (rotate the forward vector 90° clockwise from above).
const RX = -DZ;
const RZ = DX;

/** World XZ (plus a Y) for a point at `along`/`lateral` metres in the runway frame. */
export function runwayPoint(along, lateral, y = RWY.elevation) {
  return {
    x: RWY.origin.x + DX * along + RX * lateral,
    y,
    z: RWY.origin.z + DZ * along + RZ * lateral,
  };
}

/** Project a world XZ into the runway frame. Returns `{ along, lateral }`. */
export function toRunway(x, z) {
  const dx = x - RWY.origin.x;
  const dz = z - RWY.origin.z;
  return {
    along: dx * DX + dz * DZ,
    lateral: dx * RX + dz * RZ,
  };
}

/**
 * Signed heading error (radians) between a forward vector and the runway's
 * departure direction — 0 when lined up, ±π when pointing back down the runway.
 */
export function runwayHeadingError(forward) {
  // atan2 of the cross/dot of the two XZ directions, so the sign follows the
  // usual "positive = need to turn left" convention used by the autopilot.
  const cross = DX * forward.z - DZ * forward.x;
  const dot = DX * forward.x + DZ * forward.z;
  return Math.atan2(cross, dot);
}

/**
 * True when a world XZ sits on the paved runway, with `margin` metres of slack
 * added to the half-width and to each end. Used by the landing objective so a
 * touchdown a little wide of the centreline still counts.
 */
export function onRunway(x, z, margin = 0) {
  const { along, lateral } = toRunway(x, z);
  return Math.abs(lateral) <= RWY.halfWidth + margin
    && along >= RWY.approachEnd - margin
    && along <= RWY.departureEnd + margin;
}

export { RWY as RUNWAY };
